import * as React from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import FieldDecorator from './FieldDecorator';
import { ChangeableValue } from './Input';
import { hasValue, setFormFieldErrorMessage, forceTwoDigits } from '../../lib/utils';
import './fieldStyles.less';
import { blueyGrey, fieldBackgroundWhite, fieldFontSize } from '../../appComponents/cssConstants';

const MonthYearField = (props: Props) => {
	const maxMonthNumber = 12;
	const currentMonth = new Date().getMonth() + 1;
	const currentYear = new Date().getFullYear();
	const currentDay = new Date().getDate();
	const defaultMaxYear = currentYear + 10;
	const {
		label,
		className,
		value,
		onValueChange,
		minYear = currentYear,
		maxYear = defaultMaxYear,
		validateOnMount = true,
		errorMessage = null,
		...rest
	} = props;

	const [date, setDate] = React.useState(getDateFromIsoFormatString(value));
	const [month, setMonth] = React.useState(getMonthFromIsoFormatString(value));
	const [year, setYear] = React.useState(getYearFromIsoFormatString(value));

	const todayAsNumber = currentYear * 10000 + currentMonth * 100 + currentDay;

	const validateDateRange = (
		selectedMonth: string | number | undefined,
		selectedYear: string | number | undefined,
		selectedDate?: string | number | undefined
	): string | null => {
		if (!hasValue(selectedMonth) || !hasValue(selectedYear)) {
			return props.required ? `${label} is required.` : null;
		}

		const numYear = Number(selectedYear);
		const numMonth = Number(selectedMonth);
		const numDate = selectedDate && selectedDate !== '--' ? Number(selectedDate) : 1;

		const selectedAsNumber = numYear * 10000 + numMonth * 100 + numDate;

		if (selectedAsNumber < todayAsNumber) {
			return `${label} cannot be before today.`;
		}

		const maxAsNumber = maxYear * 10000 + currentMonth * 100 + currentDay;
		if (selectedAsNumber > maxAsNumber) {
			return `${label} cannot exceed ${forceTwoDigits(currentDay)}-${forceTwoDigits(currentMonth)}-${maxYear}.`;
		}

		return null;
	};

	const getAvailableMonths = (selectedYear: string | number | undefined): number[] => {
		if (!hasValue(selectedYear)) return getIncrementalNumberArray(1, maxMonthNumber);

		const numYear = Number(selectedYear);

		if (numYear < currentYear || numYear > maxYear) return [];

		if (numYear === currentYear) {
			return getIncrementalNumberArray(currentMonth, maxMonthNumber);
		}
		if (numYear === maxYear) {
			return getIncrementalNumberArray(1, currentMonth);
		}
		return getIncrementalNumberArray(1, maxMonthNumber);
	};

	const getAvailableDays = (
		selectedMonth: string | number | undefined,
		selectedYear: string | number | undefined
	): (string | number)[] => {
		if (!hasValue(selectedMonth) || !hasValue(selectedYear)) return ['--', ...getIncrementalNumberArray(1, 31)];

		const numYear = Number(selectedYear);
		const numMonth = Number(selectedMonth);
		const daysInMonth = new Date(numYear, numMonth, 0).getDate();

		if (numYear < currentYear || (numYear === currentYear && numMonth < currentMonth)) {
			return ['--'];
		}
		if (numYear === currentYear && numMonth === currentMonth) {
			return ['--', ...getIncrementalNumberArray(currentDay, daysInMonth)];
		}
		if (numYear === maxYear && numMonth === currentMonth) {
			return ['--', ...getIncrementalNumberArray(1, currentDay)];
		}
		return ['--', ...getIncrementalNumberArray(1, daysInMonth)];
	};

	const years = getIncrementalNumberArray(minYear, maxYear);
	const months = getAvailableMonths(year);
	const days = getAvailableDays(month, year);

	React.useEffect(() => {
		if (validateOnMount) {
			const initialErrorMessage = validateDateRange(month, year, date);
			onValueChange(props.name, props.value, initialErrorMessage);
		}
	}, []);

	const handleMonthChange = (event: DropDownListChangeEvent) => {
		const selectedMonth = event.target.value;
		setMonth(selectedMonth);

		const updatedDays = getAvailableDays(selectedMonth, year);

		const fallbackDay = updatedDays.length > 1 ? updatedDays[1] : '--';
		const isCurrentDateValid = hasValue(date) && date !== '--' && updatedDays.includes(Number(date));
		const newDay = isCurrentDateValid ? date : fallbackDay;
		setDate(newDay?.toString());

		if (hasValue(year)) {
			setFormFieldErrorMessage(event.target.element);
			const validationError = validateDateRange(selectedMonth, year, newDay);
			const isoFormatDate = `${year}-${forceTwoDigits(selectedMonth)}-${forceTwoDigits(newDay ?? '--')}`;
			onValueChange(props.name, isoFormatDate, validationError);
		}
	};

	const handleDateChange = (event: DropDownListChangeEvent) => {
		const selectedDate = event.target.value;
		setDate(selectedDate);

		if (!hasValue(year) || !hasValue(month)) return;

		const validationError = validateDateRange(month, year, selectedDate);
		const isoFormatDate = `${year}-${forceTwoDigits(month)}-${
			selectedDate === '--' ? '--' : forceTwoDigits(selectedDate)
		}`;
		onValueChange(props.name, isoFormatDate, validationError);
	};

	const handleYearChange = (event: DropDownListChangeEvent) => {
		const selectedYear = event.target.value;
		setYear(selectedYear);

		const updatedMonths = getAvailableMonths(selectedYear);
		const firstMonth = updatedMonths.length > 0 ? updatedMonths[0] : undefined;
		setMonth(firstMonth !== undefined ? firstMonth.toString() : '');

		const updatedDays = getAvailableDays(firstMonth, selectedYear);
		const firstDay = updatedDays.length > 1 ? updatedDays[1] : '--';
		setDate(firstDay?.toString());

		setFormFieldErrorMessage(event.target.element);
		const validationError = validateDateRange(firstMonth, selectedYear, firstDay);
		const isoFormatDate = `${selectedYear}-${forceTwoDigits(firstMonth ?? '--')}-${forceTwoDigits(firstDay ?? '--')}`;
		onValueChange(props.name, isoFormatDate, validationError);
	};

	return (
		<FieldDecorator
			label={label}
			title={props.title}
			className={classNames('monthYearField', className)}
			errorMessage={errorMessage}
		>
			<Wrapper>
				<DropDownList
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...rest}
					onChange={handleDateChange}
					value={date}
					data={days}
					defaultValue="--"
					popupSettings={{ className }}
					className="dayField"
				/>
				<DropDownList
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...rest}
					onChange={handleMonthChange}
					value={month}
					data={months}
					popupSettings={{ className }}
					className="monthField"
				/>
				<DropDownList
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...rest}
					onChange={handleYearChange}
					value={year}
					data={years}
					popupSettings={{ className }}
					className="yearField"
				/>
			</Wrapper>
		</FieldDecorator>
	);
};

export default MonthYearField;

MonthYearField.defaultProps = {
	label: null,
	title: null,
	className: null,
	required: null,
	validateOnMount: true,
	errorMessage: null,
	minYear: new Date().getFullYear(),
	maxYear: new Date().getFullYear() + 10
};

interface Props extends ChangeableValue {
	label?: string;
	title?: string;
	name: string;
	className?: string;
	value: string;
	required?: boolean;
	validateOnMount?: boolean;
	errorMessage?: string;
	minYear?: number;
	maxYear?: number;
}

const getIncrementalNumberArray = (min: number, max: number): number[] => {
	const numbers: number[] = [];
	let current = min;
	while (current <= max) {
		numbers.push(current++);
	}
	return numbers;
};

const getYearFromIsoFormatString = (date: string | number): string | undefined => {
	if (hasValue(date)) return date.toString().split('-')[0];
	return undefined;
};

const getMonthFromIsoFormatString = (date: string | number): string | undefined => {
	if (hasValue(date)) return date.toString().split('-')[1];
	return undefined;
};

const getDateFromIsoFormatString = (date: string | number): string | undefined => {
	if (hasValue(date)) return date.toString().split('-')[2];
	return undefined;
};

const Wrapper = styled.div`
	display: flex;
	justify-content: space-between;
	.k-dropdown:first-child,
	.k-dropdown:nth-child(2) {
		margin-right: 10px;
	}

	.dayField.k-dropdown.k-widget,
	.monthField.k-dropdown.k-widget,
	.yearField.k-dropdown.k-widget {
		flex: 1;
		width: unset !important;
		font-size: ${fieldFontSize};
	}

	.k-dropdown {
		border-radius: 8px;
		border-color: ${blueyGrey};

		.k-dropdown-wrap {
			.k-clear-value {
				top: 6px;
			}

			.k-input {
				background: ${fieldBackgroundWhite};
				border-radius: 8px;
			}
		}
	}
`;
